import React, { useEffect, useState } from "react";
import { Box, Button, Stack, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import ConfirmModal from "../../components/common/ConfirmModal";
import PageHeader from "../../components/common/PageHeader";
import DataTable from "../../components/common/DataTable";
import StatusChip from "../../components/common/StatusChip";

import {
  BOOKING_STRINGS,
  COMMON_STRINGS,
  MENU_STRINGS,
  STATUS_CONFIG,
} from "../../constants/strings";

import { getBookings, updateBookingStatus } from "../../services/api";

import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice";

import "../table-common.css";

const BookingListPage = () => {
  const dispatch = useDispatch();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const getMapUrl = (lat?: number, lng?: number) => {
    if (!lat || !lng) return "";
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  };

  // ================= FETCH =================
  const fetchBookings = async () => {
    try {
      setLoading(true);
      const res = await getBookings();
      setRows(res.data.data || []);
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  // ================= UPDATE =================
  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      setRows((prev) =>
        prev.map((row) =>
          row._id === id ? { ...row, status: newStatus } : row,
        ),
      );

      await updateBookingStatus(id, newStatus);

      dispatch(
        showSnackbar({
          message: "Booking updated successfully",
          type: "success",
        }),
      );
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
          type: "error",
        }),
      );
      fetchBookings();
    }
  };

  const handleConfirmCancel = () => {
    if (selectedId) {
      handleStatusChange(selectedId, STATUS_CONFIG.CANCELLED.value);
    }
    setConfirmOpen(false);
  };

  // ================= FILTER =================
  const filteredRows = rows.filter((row) => {
    const matchSearch = [
      row._id,
      row.userId?.name,
      row.userId?.mobile,
      row.serviceId?.subSubCategory,
      row.status,
      row.date,
      row.time,
    ]
      .join(" ")
      .toLowerCase()
      .includes(searchText.toLowerCase());

    const matchStatus = statusFilter ? row.status === statusFilter : true;

    return matchSearch && matchStatus;
  });

  // ================= COLUMNS =================
  const columns: GridColDef[] = [
    {
      field: "user",
      headerName: BOOKING_STRINGS.USER,
      flex: 1,
      renderCell: (params) => params.row.userId?.name || "Guest User",
    },
    {
      field: "address",
      headerName: "Address",
      flex: 2,
      renderCell: (params) => {
        const address = params.row.address;
        if (!address) return "N/A";

        const addressParts = [
          address.houseNo,
          address.city,
          address.state,
          address.pincode,
        ]
          .map((item) => item?.toString().trim())
          .filter((item) => item);

        return addressParts.length > 0
          ? addressParts.join(", ")
          : address.fullAddress || "N/A";
      },
    },
    {
      field: "service",
      headerName: MENU_STRINGS.BOOKINGS,
      flex: 1,
      renderCell: (params) => params.row.serviceId?.subSubCategory || "N/A",
    },
    {
      field: "date",
      headerName: BOOKING_STRINGS.DATE,
      flex: 1,
      renderCell: (params) =>
        new Date(params.row.date).toLocaleDateString("en-IN"),
    },
    {
      field: "time",
      headerName: "Time",
      flex: 1,
      renderCell: (params) => params.row.time || "N/A",
    },
    {
      field: "status",
      headerName: BOOKING_STRINGS.STATUS,
      flex: 1,
      renderCell: (params) => <StatusChip status={params.value} />,
    },

    // 🔥 ACTIONS UPDATED
    {
      field: "actions",
      headerName: BOOKING_STRINGS.ACTIONS,
      flex: 2,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const currentStatus = params.row.status;

        const isLocked =
          currentStatus === STATUS_CONFIG.COMPLETED.value ||
          currentStatus === STATUS_CONFIG.CANCELLED.value;

        return (
          <Box className="action-cell">
            <Stack direction="row" spacing={0.5}>
              {/* 📋 COPY */}
              <Tooltip title="Copy Location">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const lat = params.row.address?.latitude;
                      const lng = params.row.address?.longitude;

                      const url = getMapUrl(lat, lng);

                      if (url) {
                        navigator.clipboard.writeText(url);
                        dispatch(
                          showSnackbar({
                            message: "Location copied!",
                            type: "success",
                          }),
                        );
                      } else {
                        dispatch(
                          showSnackbar({
                            message: "Location not available",
                            type: "warning",
                          }),
                        );
                      }
                    }}
                    sx={{ minWidth: "32px", padding: "6px" }}
                  >
                    📋
                  </Button>
                </span>
              </Tooltip>

              {/* 📲 WHATSAPP */}
              {/* 📲 WHATSAPP */}
              <Tooltip title="Send WhatsApp">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    onClick={() => {
                      const address = params.row.address;

                      if (!address) {
                        dispatch(
                          showSnackbar({
                            message: "Address not available",
                            type: "warning",
                          }),
                        );
                        return;
                      }

                      // 📍 Booking ki actual location
                      const lat = address.latitude;
                      const lng = address.longitude;

                      const mapUrl = getMapUrl(lat, lng);

                      // 🏠 Booking address
                      const addressText =
                        address.fullAddress ||
                        [
                          address.houseNo,
                          address.landmark,
                          address.city,
                          address.state,
                          address.pincode,
                        ]
                          .map((item) => item?.toString().trim())
                          .filter(Boolean)
                          .join(", ") ||
                        "Address not available";

                      // 📲 WhatsApp message
                      const message = `📍 Service Location

🏠 Address:
${addressText}

🗺️ Google Maps Location:
${mapUrl || "Location not available"}`;

                      window.open(
                        `https://wa.me/?text=${encodeURIComponent(message)}`,
                        "_blank",
                      );
                    }}
                    sx={{ minWidth: "32px", padding: "6px" }}
                  >
                    📲
                  </Button>
                </span>
              </Tooltip>

              {/* 🔵 IN PROGRESS */}
              <Tooltip
                title={
                  currentStatus === STATUS_CONFIG.IN_PROGRESS.value
                    ? "Already in progress"
                    : currentStatus === STATUS_CONFIG.COMPLETED.value
                      ? "Already completed"
                      : currentStatus === STATUS_CONFIG.CANCELLED.value
                        ? "Booking cancelled"
                        : "Start Service"
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={currentStatus !== STATUS_CONFIG.CONFIRMED.value}
                    onClick={() =>
                      handleStatusChange(
                        params.row._id,
                        STATUS_CONFIG.IN_PROGRESS.value,
                      )
                    }
                    sx={{
                      minWidth: "32px",
                      padding: "6px",
                      backgroundColor: STATUS_CONFIG.IN_PROGRESS.color,
                    }}
                  >
                    <PlayArrowIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>

              {/* ✅ COMPLETE */}
              <Tooltip
                title={
                  currentStatus === STATUS_CONFIG.COMPLETED.value
                    ? "Already completed"
                    : currentStatus === STATUS_CONFIG.CANCELLED.value
                      ? "Booking cancelled"
                      : "Mark as Completed"
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={
                      currentStatus !== STATUS_CONFIG.IN_PROGRESS.value &&
                      currentStatus !== STATUS_CONFIG.CONFIRMED.value
                    }
                    onClick={() =>
                      handleStatusChange(
                        params.row._id,
                        STATUS_CONFIG.COMPLETED.value,
                      )
                    }
                    sx={{
                      minWidth: "32px",
                      padding: "6px",
                      backgroundColor: STATUS_CONFIG.COMPLETED.color,
                    }}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>

              {/* ❌ CANCEL */}
              <Tooltip
                title={
                  currentStatus === STATUS_CONFIG.CANCELLED.value
                    ? "Already cancelled"
                    : currentStatus === STATUS_CONFIG.COMPLETED.value
                      ? "Already completed"
                      : "Cancel Booking"
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={
                      currentStatus === STATUS_CONFIG.COMPLETED.value ||
                      currentStatus === STATUS_CONFIG.CANCELLED.value
                    }
                    onClick={() => {
                      setSelectedId(params.row._id);
                      setConfirmOpen(true);
                    }}
                    sx={{
                      minWidth: "32px",
                      padding: "6px",
                      color: STATUS_CONFIG.CANCELLED.color,
                      borderColor: STATUS_CONFIG.CANCELLED.color,
                    }}
                  >
                    <CancelIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <PageHeader
        title={BOOKING_STRINGS.TITLE}
        searchValue={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder={BOOKING_STRINGS.SEARCH_PLACEHOLDER}
        filterValue={statusFilter}
        onFilterChange={setStatusFilter}
        filterOptions={[
          { label: "All Status", value: "" },
          {
            label: STATUS_CONFIG.PENDING.label,
            value: STATUS_CONFIG.PENDING.value,
          },
          {
            label: STATUS_CONFIG.CONFIRMED.label,
            value: STATUS_CONFIG.CONFIRMED.value,
          },
          {
            label: STATUS_CONFIG.IN_PROGRESS.label,
            value: STATUS_CONFIG.IN_PROGRESS.value,
          },
          {
            label: STATUS_CONFIG.COMPLETED.label,
            value: STATUS_CONFIG.COMPLETED.value,
          },
          {
            label: STATUS_CONFIG.CANCELLED.label,
            value: STATUS_CONFIG.CANCELLED.value,
          },
        ]}
      />

      <DataTable
        rows={filteredRows}
        columns={columns}
        loading={loading}
        getRowId={(row) => row._id}
      />

      <ConfirmModal
        open={confirmOpen}
        title={BOOKING_STRINGS.CONFIRM_CANCEL_TITLE}
        description={BOOKING_STRINGS.CONFIRM_CANCEL_DESC}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmCancel}
      />
    </Box>
  );
};

export default BookingListPage;
